import type { JSX } from 'react';
import { DateTime } from '@ksyos/date-time';
import { Card } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ObesiQQuestionnaireName } from '@common/customControls/bariatrics';
import type {
    IProcessQuestionnaireResponse,
    IProcessQuestionnaireResponseItem,
} from '@common/interfaces/api/processQuestionnaireResponsesApi';
import type { IQuestionnaireItem } from '@common/interfaces/api/questionnairesApi';
import type { IMonitoringChartItem } from '@frontend/components/pages/monitoring/MonitoringChart';
import { obesiQThresholds, obesiQTranslations } from '@common/customControls/bariatrics';
import { calculateMonthsAndDaysDifference } from '@common/utils/calculateMonthsAndDaysDifferences';
import { ObesiQMonitoringChartHeader } from '@frontend/components/pages/episodeCategory/bariatrics/obesiQ/ObesiQMonitoringChartHeader';
import { MonitoringCard } from '@frontend/components/pages/monitoring/MonitoringCard';
import { MonitoringCardFooter } from '@frontend/components/pages/monitoring/MonitoringCardFooter';
import { MonitoringChart } from '@frontend/components/pages/monitoring/MonitoringChart';
import { getProcessQuestionnaireResponses } from '@frontend/services/api/processesApiService';
import { getQuestionnaireItems } from '@frontend/services/api/questionnairesApiService';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { ObesiQCardFooter } from './ObesiQCardFooter';

export interface IQuestionnaire {
    hasWarning: boolean;
    id?: string;
    isChecked: boolean;
    name: ObesiQQuestionnaireName;
}

type ObesiQCardProps = Readonly<{
    processId: string;
    questionnaire: IQuestionnaire;
    surgeryDate: Date | string;
}>;

const xAxisTicks = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
const yAxisTicks = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

export function ObesiQCard(props: ObesiQCardProps): JSX.Element {
    const { processId, questionnaire, surgeryDate } = props;
    const abortSignal = useAbortSignal();
    const [monitoringChartItems, setMonitoringChartItems] = useState<IMonitoringChartItem[]>([]);
    const [isEmpty, setIsEmpty] = useState(true);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [processQuestionnaireResponses, setProcessQuestionnaireResponses] = useState<IProcessQuestionnaireResponse[]>(
        []
    );
    const [questionnaireItems, setQuestionnaireItems] = useState<IQuestionnaireItem[]>([]);
    const [selectedMonitoringChartItem, setSelectedMonitoringChartItem] = useState<IMonitoringChartItem>();
    const questionnaireTitle = obesiQTranslations[questionnaire.name].title;

    useEffect(() => {
        loadQuestionnaire();
    }, [questionnaire]);

    useEffect(() => {
        loadMonitoringChartItems();
    }, [processQuestionnaireResponses]);

    return (
        <MonitoringCard
            emptyTitle={`Er zijn nog geen patiëntgegevens voor ${questionnaireTitle.toLowerCase()} bekend`}
            isEmpty={isEmpty}
            isLoading={isLoading}
        >
            <Card.Body>
                <ObesiQMonitoringChartHeader
                    showWarning={questionnaire.hasWarning}
                    title={obesiQTranslations[questionnaire.name].title}
                />
                <MonitoringChart
                    disableTooltip={true}
                    grid="horizontal"
                    height={300}
                    iconBasedOnAverage={true}
                    items={monitoringChartItems}
                    onDotClick={handleDotClick}
                    referenceValue={obesiQThresholds[questionnaire.name]}
                    selectedItem={isOpen ? selectedMonitoringChartItem : undefined}
                    XAxisLabel="Maanden na operatie"
                    XAxisTicks={xAxisTicks}
                    YAxisLabel="Score"
                    YAxisTicks={yAxisTicks}
                />
            </Card.Body>
            <MonitoringCardFooter
                isClosedText="Toon antwoorden op vragenlijsten"
                isOpen={isOpen}
                isOpenText="Klap de vragenlijst in"
                onClick={() => setIsOpen(!isOpen)}
            >
                <ObesiQCardFooter
                    onPageChange={handlePageChange}
                    page={getPage()}
                    processQuestionnaireResponses={processQuestionnaireResponses}
                    questionnaireItems={questionnaireItems}
                    questionnaireName={questionnaire.name}
                />
            </MonitoringCardFooter>
        </MonitoringCard>
    );

    function findRaschTransformedScoreItem(
        processQuestionnaireResponse: IProcessQuestionnaireResponse
    ): IProcessQuestionnaireResponseItem | undefined {
        return processQuestionnaireResponse.items?.find((item) => item.text === 'raschTransformedScore');
    }

    function isProcessQuestionnaireResponseMonitoringChartItem(
        processQuestionnaireResponse: IProcessQuestionnaireResponse,
        monitoringChartItem: IMonitoringChartItem
    ): boolean {
        const raschTransformedScoreItem = findRaschTransformedScoreItem(processQuestionnaireResponse);

        return (
            processQuestionnaireResponse.performedDateTime === monitoringChartItem.date &&
            Number(raschTransformedScoreItem?.answer.value) === monitoringChartItem.value
        );
    }

    function handleDotClick(monitoringChartItem: IMonitoringChartItem): void {
        if (isOpen) {
            setIsOpen(monitoringChartItem !== selectedMonitoringChartItem);
        } else {
            setIsOpen(true);
        }
        setSelectedMonitoringChartItem(monitoringChartItem);
    }

    function handlePageChange(page: number): void {
        const monitoringChartItem = monitoringChartItems.find((monitoringChartItemElement) =>
            isProcessQuestionnaireResponseMonitoringChartItem(
                processQuestionnaireResponses[page - 1],
                monitoringChartItemElement
            )
        );
        setSelectedMonitoringChartItem(monitoringChartItem);
    }

    async function loadQuestionnaireItems(questionnaireId: string): Promise<void> {
        const { items } = await getQuestionnaireItems(
            questionnaireId,
            {
                includeAnswerOptions: true,
            },
            abortSignal
        );
        setQuestionnaireItems(items);
    }

    async function loadQuestionnaireResponseItems(): Promise<void> {
        const query = {
            questionnaireNames: [questionnaire.name],
        };
        const { items } = await getProcessQuestionnaireResponses(processId, query, abortSignal);
        const filteredItems = items.filter((item) => item.performedDateTime >= surgeryDate);
        setProcessQuestionnaireResponses(filteredItems);
    }

    async function loadQuestionnaire(): Promise<void> {
        setIsLoading(true);
        if (questionnaire.id) {
            await Promise.all([loadQuestionnaireItems(questionnaire.id), loadQuestionnaireResponseItems()]);
        }
        setIsLoading(false);
    }

    function loadMonitoringChartItems(): void {
        const nextMonitoringChartItems: IMonitoringChartItem[] = [];
        for (const processQuestionnaireResponse of processQuestionnaireResponses) {
            const { performedDateTime } = processQuestionnaireResponse;
            const raschTransformedScoreItem = findRaschTransformedScoreItem(processQuestionnaireResponse);
            if (!raschTransformedScoreItem) {
                continue;
            }

            const difference = calculateMonthsAndDaysDifference(
                DateTime.from(surgeryDate),
                DateTime.from(performedDateTime)
            );
            if (difference.months >= 0) {
                nextMonitoringChartItems.push({
                    date: performedDateTime,
                    difference,
                    value: Number(raschTransformedScoreItem.answer.value),
                });
            }
        }
        setMonitoringChartItems(nextMonitoringChartItems);
        setIsEmpty(nextMonitoringChartItems.length === 0);
        setSelectedMonitoringChartItem(nextMonitoringChartItems.at(-1));
    }

    function getPage(): number {
        if (!selectedMonitoringChartItem) {
            return 0;
        }

        const index = processQuestionnaireResponses.findIndex((processQuestionnaireResponse) =>
            isProcessQuestionnaireResponseMonitoringChartItem(processQuestionnaireResponse, selectedMonitoringChartItem)
        );

        return index > -1 ? index + 1 : 0;
    }
}
